import React, { useState, useRef, useEffect } from 'react';
import axios from '../../lib/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { JAZMIN_AVATAR_URL as JAZMIN_AVATAR } from '../../data/cdaContent';
import { useLang } from '../../i18n/cda';

const SESSION_KEY = 'cda_jazmin_session';

const getSessionId = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

const GREETING = "Hi, I'm Jazmin — your CDA career coach! 🌟 I'm here to guide you step by step to your CDA credential. You've got this. What can I help you with today?";

const SUGGESTIONS = [
  "Which CDA setting should I choose?",
  "How do I get my 480 hours?",
  "What goes in my portfolio binder?",
  "How can I pay for the $525 fee?",
];

export const JazminChat = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const endRef = useRef(null);

  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    if (open && messages.length === 0) {
      axios.get(`/cda/chat/${sessionId.current}`).then((res) => {
        if (res.data?.length) {
          setMessages(res.data.map((m) => ({ role: m.role, content: m.content })));
        } else {
          setMessages([{ role: 'assistant', content: t(GREETING) }]);
        }
      }).catch(() => {
        setMessages([{ role: 'assistant', content: t(GREETING) }]);
      });
    }
  }, [open]);

  useEffect(() => { if (open) scrollToBottom(); }, [messages, open, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post('/cda/chat', { session_id: sessionId.current, message: msg, lang });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'assistant', content: t('Sorry, I had trouble responding. Please try again in a moment.') }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          data-testid="jazmin-open-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 pl-2 pr-5 py-2 rounded-full bg-[#0d7d6f] text-white shadow-xl hover:bg-[#0a6358] transition-colors"
        >
          <img src={JAZMIN_AVATAR} alt="Jazmin" className="w-10 h-10 rounded-full object-cover border-2 border-white" />
          <span className="font-semibold text-sm">{t('Ask Jazmin')}</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div data-testid="jazmin-chat-panel" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[400px] h-[600px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-4 bg-[#0d7d6f] text-white">
            <div className="flex items-center gap-3">
              <img src={JAZMIN_AVATAR} alt="Jazmin" className="w-11 h-11 rounded-full object-cover border-2 border-white/70" />
              <div>
                <p className="font-bold leading-tight">Jazmin</p>
                <p className="text-xs text-emerald-100 flex items-center gap-1"><Sparkles className="w-3 h-3" /> {t('Your CDA Career Coach')}</p>
              </div>
            </div>
            <button data-testid="jazmin-close-button" onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-white/20">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#0d7d6f] text-white rounded-br-sm' : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-2 bg-slate-50">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(t(s))} className="text-xs px-3 py-1.5 bg-white border border-emerald-200 text-emerald-800 rounded-full hover:bg-emerald-50">
                  {t(s)}
                </button>
              ))}
            </div>
          )}

          <div className="p-3 border-t border-slate-200 bg-white flex items-center gap-2">
            <Input
              data-testid="jazmin-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder={t('Ask Jazmin anything about your CDA...')}
              className="flex-1 h-10"
            />
            <Button data-testid="jazmin-send-button" onClick={() => send()} disabled={loading} size="icon" className="h-10 w-10 rounded-full bg-[#0d7d6f] hover:bg-[#0a6358]">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default JazminChat;
